import React from 'react';

// 비로그인 업로드 페이지의 "행사 승인 현황" 달력. owner 박스 항목 중 이번 달·다음 달
// 행사일(event_start_at)에 썸네일을 표시. 서버가 반환한 이벤트를 로컬 날짜로 그룹핑한다.

export interface CalEvent {
  fileHash: string;
  eventStartAt: string; // ISO
  thumbnailPath: string | null;
  filename: string | null;
  boxName: string | null;
}

const WD = ['일', '월', '화', '수', '목', '금', '토'];
const pad = (n: number) => String(n).padStart(2, '0');
const dateKeyLocal = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// onSelectDate: 날짜 칸 클릭 → 그 날짜를 입력값으로(비로그인 업로드 페이지).
// onSelectEvents: 영상이 있는 칸 클릭 → 그 날의 이벤트들을 넘긴다(영상 관리 페이지의 상세 모달).
// 둘 다 옵셔널이며, 영상이 있는 칸에서는 onSelectEvents 가 우선한다(상세 보기가 더 구체적인 의도).
// onDropVideo: 영상 카드(application/x-video-drag)를 날짜 칸에 드롭 → 편성 문진(퀵UI).
//   ⚠️ opt-in — prop 이 없으면 드래그 핸들러 자체를 달지 않는다(무인증 EmbedCalendar·
//   비로그인 PublicUpload 에는 드롭 UI 가 노출되면 안 된다).
export interface VideoDropPayload { fileHash: string; title: string; }

const VIDEO_DRAG_TYPE = 'application/x-video-drag';

const MonthGrid: React.FC<{ year: number; month: number; byDate: Map<string, CalEvent[]>; todayKey: string; onSelectDate?: (dateKey: string) => void; onSelectEvents?: (events: CalEvent[]) => void; onDropVideo?: (dateKey: string, payload: VideoDropPayload) => void }> = ({ year, month, byDate, todayKey, onSelectDate, onSelectEvents, onDropVideo }) => {
  const firstDay = new Date(year, month, 1);
  const startWd = firstDay.getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < startWd; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="w-full">
      <div className="text-center text-sm font-semibold text-gray-700 mb-1">{year}년 {month + 1}월</div>
      <div className="grid grid-cols-7 gap-px bg-gray-100 border border-gray-100">
        {WD.map((w, i) => (
          <div key={w} className={`text-center text-xs py-1 bg-gray-50 ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400'}`}>{w}</div>
        ))}
        {cells.map((day, idx) => {
          if (day === null) return <div key={idx} className="aspect-square bg-white" />;
          const key = `${year}-${pad(month + 1)}-${pad(day)}`;
          const evs = byDate.get(key);
          const firstEv = evs?.[0];
          const isToday = key === todayKey;
            const canOpen = !!onSelectEvents && !!evs && evs.length > 0;
            const onClick = canOpen ? () => onSelectEvents!(evs!)
                          : onSelectDate ? () => onSelectDate(key)
                          : undefined;
            const names = evs?.map((e) => e.filename || '').filter(Boolean).join(', ');
            const dropHandlers = onDropVideo ? {
              onDragOver: (e: React.DragEvent) => {
                if (Array.from(e.dataTransfer.types).includes(VIDEO_DRAG_TYPE)) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'copy';
                  (e.currentTarget as HTMLElement).classList.add('ring-2', 'ring-emerald-400', 'ring-inset');
                }
              },
              onDragLeave: (e: React.DragEvent) => {
                (e.currentTarget as HTMLElement).classList.remove('ring-2', 'ring-emerald-400', 'ring-inset');
              },
              onDrop: (e: React.DragEvent) => {
                (e.currentTarget as HTMLElement).classList.remove('ring-2', 'ring-emerald-400', 'ring-inset');
                const raw = e.dataTransfer.getData(VIDEO_DRAG_TYPE);
                if (!raw) return;
                e.preventDefault();
                try {
                  const payload = JSON.parse(raw);
                  if (payload?.fileHash) onDropVideo(key, { fileHash: payload.fileHash, title: payload.title ?? '' });
                } catch { /* 형식 밖 드래그는 무시 */ }
              },
            } : {};
            return (
            <div
              key={idx}
              onClick={onClick}
              {...dropHandlers}
              className={`relative aspect-square bg-white overflow-hidden ${isToday ? 'ring-2 ring-blue-500 ring-inset z-10' : ''} ${onClick ? 'cursor-pointer hover:ring-2 hover:ring-blue-400 hover:ring-inset' : ''}`}
              title={canOpen ? `${names}${evs!.length > 1 ? ` (${evs!.length}건 — 첫 영상이 열립니다)` : ''}`
                    : onSelectDate ? '클릭하면 행사 시작 시각에 이 날짜가 입력됩니다'
                    : names}
            >
              {firstEv?.thumbnailPath && (
                <img src={`/mofile/thumbnail/${firstEv.thumbnailPath}`} alt="" className="absolute inset-0 w-full h-full object-cover" />
              )}
              <span className={`absolute top-0 left-1 text-xs font-medium ${isToday ? 'bg-blue-500 text-white px-1 rounded-b' : firstEv ? 'text-white drop-shadow' : 'text-gray-400'}`}>{day}</span>
              {evs && evs.length > 1 && (
                <span className="absolute bottom-0 right-0 text-[10px] bg-black/60 text-white px-1 rounded-tl">+{evs.length - 1}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// twoColumn: 두 달을 가로로 나란히(넓은 페이지용 — 영상 관리). 기본 세로 스택
// (비로그인 업로드 max-w-lg·임베드 위젯 등 좁은 컨테이너 보호).
const UploadCalendar: React.FC<{ events: CalEvent[]; handle?: string | null; twoColumn?: boolean; onSelectDate?: (dateKey: string) => void; onSelectEvents?: (events: CalEvent[]) => void; onDropVideo?: (dateKey: string, payload: VideoDropPayload) => void }> = ({ events, handle, twoColumn, onSelectDate, onSelectEvents, onDropVideo }) => {
  const byDate = new Map<string, CalEvent[]>();
  for (const e of events) {
    const k = dateKeyLocal(new Date(e.eventStartAt));
    const arr = byDate.get(k) ?? [];
    arr.push(e);
    byDate.set(k, arr);
  }
  const now = new Date();
  const todayKey = dateKeyLocal(now);
  const y = now.getFullYear();
  const m = now.getMonth();
  const nextY = m === 11 ? y + 1 : y;
  const nextM = m === 11 ? 0 : m + 1;

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-2">{handle ? `${handle} 행사 현황` : '행사 현황'}</h3>
      <div className={twoColumn ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : 'flex flex-col gap-4'}>
        <MonthGrid year={y} month={m} byDate={byDate} todayKey={todayKey} onSelectDate={onSelectDate} onSelectEvents={onSelectEvents} onDropVideo={onDropVideo} />
        <MonthGrid year={nextY} month={nextM} byDate={byDate} todayKey={todayKey} onSelectDate={onSelectDate} onSelectEvents={onSelectEvents} onDropVideo={onDropVideo} />
      </div>
      {events.length === 0 && <p className="text-xs text-gray-400 mt-2">이번 달·다음 달 등록된 행사가 없습니다.</p>}
    </div>
  );
};

export default UploadCalendar;
